import {
    Alert,
    Box,
    Col,
    Divider,
    FormGroup,
    FormParagraph,
    Radio,
    RadioGroup,
    Row,
    Text,
    TextArea,
} from '@ksyos/design-system';
import { styled } from 'styled-components';
import type { ILeidseSchema } from '../../../../../common/interfaces/ophthalmology/orthO/orthOProcedureCustomControlData';
import type { IOrthOV1ProcedureExaminationsProps } from './OrthOV1ProcedureExaminations';
import { OogmotiliteitOption } from '../../../../../common/validation/customControls/ophthalmology';
import { getValidationErrorTranslation } from '../../../../utils/getValidationErrorTranslation';
import { CircleCountHeading } from '../../../elements/CircleCountHeading';
import { InputText } from '../../../elements/common';
import { OrthOExaminationOrder } from './orthOExaminationOrder';
import { initialLeidseschemaFields } from './orthOV1ProcedureInitializeCustomControlData';
import { ReadOrEditInputField } from './ReadOrEditInputField';

export function OrthOV1ProcedureOcularMotility(props: IOrthOV1ProcedureExaminationsProps) {
    const { customControlData, isOrthOFol, isReadOnly, errors } = props;

    return (
        <>
            <CircleCountHeading
                count="3"
                label="Oogmotiliteit"
            />
            <ReadOrEditInputField
                {...props}
                error={errors?.get('customControlData.convergentietest')}
                fieldKey="convergentietest"
                hasErrors={errors?.has('customControlData.convergentietest')}
                isRequired={!isOrthOFol}
                label="orthOProcedure.convergencetest"
                placeholder="Bevindingen..."
                tabIndex={OrthOExaminationOrder.ocularMotilityExamination}
                value={customControlData.convergentietest}
            />

            {!isReadOnly && (
                <FormGroup
                    hasErrors={errors?.has('customControlData.oogbewegingen')}
                    isRequired={!isOrthOFol}
                    label="Oogbewegingen"
                >
                    <RadioGroup
                        error={getValidationErrorTranslation(errors?.get('customControlData.oogbewegingen'))}
                        layout="row"
                        name="oogbewegingen"
                        onChange={async (e) => {
                            handleChangeOogBewegingen(e.target.value);
                        }}
                        value={customControlData.oogbewegingen ?? ''}
                    >
                        <Radio
                            label="Bijzonderheden"
                            value={OogmotiliteitOption.bijzonderheden}
                        />
                        <Radio
                            label="Geen bijzonderheden"
                            value={OogmotiliteitOption.geenBijzonderheden}
                        />
                    </RadioGroup>
                </FormGroup>
            )}
            {isReadOnly && customControlData.oogbewegingen && (
                <FormGroup label="Oogbewegingen">
                    <FormParagraph>{customControlData.oogbewegingen}</FormParagraph>
                </FormGroup>
            )}

            {customControlData.oogbewegingen === OogmotiliteitOption.bijzonderheden && (
                <>
                    {(!isReadOnly || Object.values(customControlData.leidse_schema).some((value) => value)) && (
                        <FormGroup
                            hasErrors={errors?.has('customControlData.leidse_schema')}
                            isRequired
                            label="Leidse schema"
                        >
                            <Row
                                columnGap={[6, null, null, 10]}
                                rowGap={[6, null, null, 10]}
                            >
                                <Col span={[12, null, 6]}>
                                    <Row style={{ borderBottom: '1px solid #50463f' }}>
                                        <Col
                                            span={6}
                                            style={{ borderRight: '1px solid #50463f' }}
                                        >
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                minHeight="72px"
                                                px={6}
                                                py={4}
                                            >
                                                {renderLeidseSchemaContent('leidse_schema_1')}
                                            </Box>
                                        </Col>
                                        <Col span={6}>
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                minHeight="72px"
                                                px={6}
                                                py={4}
                                            >
                                                {renderLeidseSchemaContent('leidse_schema_2')}
                                            </Box>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col
                                            span={6}
                                            style={{ borderRight: '1px solid #50463f' }}
                                        >
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                minHeight="72px"
                                                px={6}
                                                py={4}
                                            >
                                                {renderLeidseSchemaContent('leidse_schema_3')}
                                            </Box>
                                        </Col>
                                        <Col span={6}>
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                minHeight="72px"
                                                px={6}
                                                py={4}
                                            >
                                                {renderLeidseSchemaContent('leidse_schema_4')}
                                            </Box>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col span={[12, null, 6]}>
                                    <Row>
                                        <Col
                                            span={4}
                                            style={{ alignSelf: 'center' }}
                                        >
                                            <Box
                                                display="flex"
                                                justifyContent="right"
                                                paddingRight={6}
                                            >
                                                {renderLeidseSchemaContent('leidse_schema_5')}
                                            </Box>
                                        </Col>
                                        <Col span={4}>
                                            <Row>
                                                <Col span={12}>
                                                    <Box
                                                        alignItems="flex-end"
                                                        borderBottom="1px solid #50463f"
                                                        display="flex"
                                                        justifyContent="center"
                                                        minHeight="72px"
                                                        paddingBottom={4}
                                                    >
                                                        {renderLeidseSchemaContent('leidse_schema_6')}
                                                    </Box>
                                                </Col>
                                                <Col span={12}>
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        minHeight="72px"
                                                        paddingTop={4}
                                                    >
                                                        {renderLeidseSchemaContent('leidse_schema_7')}
                                                    </Box>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col
                                            span={4}
                                            style={{ alignSelf: 'center' }}
                                        >
                                            <Box
                                                display="flex"
                                                justifyContent="left"
                                                paddingLeft={6}
                                            >
                                                {renderLeidseSchemaContent('leidse_schema_8')}
                                            </Box>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col span={[12, null, 6]}>
                                    <div style={{ overflow: 'hidden' }}>
                                        <BorderRadiusBox>
                                            <Row style={{ padding: '0 24px' }}>
                                                <Col
                                                    span={6}
                                                    style={{ borderRight: '1px solid #50463f' }}
                                                >
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        minHeight="144px"
                                                        paddingBottom={12}
                                                        paddingTop={14}
                                                        px={6}
                                                    >
                                                        {renderLeidseSchemaContent('leidse_schema_9')}
                                                    </Box>
                                                </Col>
                                                <Col span={6}>
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        minHeight="144px"
                                                        paddingBottom={12}
                                                        paddingTop={14}
                                                        px={6}
                                                    >
                                                        {renderLeidseSchemaContent('leidse_schema_10')}
                                                    </Box>
                                                </Col>
                                            </Row>
                                        </BorderRadiusBox>
                                    </div>
                                </Col>

                                <Col span={12}>
                                    {isReadOnly && (
                                        <Text
                                            fontWeight="medium"
                                            size="sm"
                                        >
                                            {customControlData.leidse_schema.leidse_schema_patroon} - patroon
                                        </Text>
                                    )}
                                    {!isReadOnly && (
                                        <Box display="flex">
                                            <Box width="100%">
                                                <InputText
                                                    onChange={(value) =>
                                                        handleChangeLeidseSchema('leidse_schema_patroon', value)
                                                    }
                                                    placeholder="Vul een patroon in..."
                                                    value={customControlData.leidse_schema.leidse_schema_patroon ?? ''}
                                                />
                                            </Box>
                                            <Text
                                                size="sm"
                                                sx={{ flexShrink: 0 }}
                                            >
                                                - patroon
                                            </Text>
                                        </Box>
                                    )}
                                </Col>
                            </Row>
                        </FormGroup>
                    )}

                    {errors && errors.has('customControlData.leidse_schema') && (
                        <Alert appearance="warning">Vul minimaal één veld bij het Leidse schema</Alert>
                    )}

                    {(!isReadOnly || customControlData.leidse_schema_toelichting) && (
                        <FormGroup
                            hasErrors={errors?.has('customControlData.leidse_schema_toelichting')}
                            label="Toelichting"
                        >
                            {props.isReadOnly && (
                                <FormParagraph>{customControlData.leidse_schema_toelichting}</FormParagraph>
                            )}
                            {!props.isReadOnly && (
                                <InputText
                                    error={errors?.get('customControlData.leidse_schema_toelichting')}
                                    onChange={async (value) =>
                                        await props.handleChangeCustomControl({ leidse_schema_toelichting: value })
                                    }
                                    onDoValidate={async () =>
                                        props.validateOneCustomControl('leidse_schema_toelichting')
                                    }
                                    placeholder="Toelichting of opmerking..."
                                    value={customControlData.leidse_schema_toelichting ?? ''}
                                />
                            )}
                        </FormGroup>
                    )}
                </>
            )}

            <Divider
                color="minimal"
                spacing="medium"
            />
        </>
    );

    function renderLeidseSchemaContent(fieldKey: keyof ILeidseSchema) {
        if (isReadOnly) {
            return (
                <Box maxWidth="90px">
                    <FormParagraph>{customControlData.leidse_schema[fieldKey]}</FormParagraph>
                </Box>
            );
        }

        return (
            <Box maxWidth="90px">
                <TextArea
                    error={getValidationErrorTranslation(errors?.get(`customControlData.leidse_schema.${fieldKey}`))}
                    onChange={(event) => handleChangeLeidseSchema(fieldKey, event.target.value)}
                    placeholder="XX.."
                    value={customControlData.leidse_schema[fieldKey] ?? ''}
                />
            </Box>
        );
    }

    async function handleChangeOogBewegingen(value: string) {
        if (!props.isReadOnly) {
            await props.handleChangeCustomControl({ oogbewegingen: value });
            props.validateOneCustomControl('oogbewegingen');

            if (value === OogmotiliteitOption.geenBijzonderheden) {
                await props.handleChangeCustomControl({
                    leidse_schema: { ...initialLeidseschemaFields },
                    leidse_schema_toelichting: null,
                });
                props.validateOneCustomControl('leidse_schema');
            }
        }
    }

    async function handleChangeLeidseSchema(name: keyof ILeidseSchema, value: string) {
        if (!props.isReadOnly) {
            if (props.handleChangeCustomControl) {
                await props.handleChangeCustomControl({
                    leidse_schema: {
                        ...customControlData.leidse_schema,
                        [name]: value ? value : null,
                    },
                });

                props.validateOneCustomControl('leidse_schema');
            }
        }
    }
}

const BorderRadiusBox = styled(Box)`
    border-top: 1px solid #50463f;
    border-top-left-radius: 50% 50%;
    border-bottom: none;
    border-top-right-radius: 50% 50%;
    position: relative;
    margin: 0 -24px;
`;
